import { useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { apiClient, type ApiResponse } from '@/lib/api-client';

interface PortalSettingsData {
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  fontColor: string;
  firmName: string;
  welcomeMessage: string;
  termsOfService: string;
  portalLinkExpiryDays: number;
}

const MAX_LOGO_SIZE_MB = 2;
const ACCEPTED_LOGO_FORMATS = ['image/png', 'image/svg+xml'];

/**
 * PortalSettings — client portal branding and configuration.
 * Requirements: 9.13, 15.7
 */
export function PortalSettings() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoError, setLogoError] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'portal'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PortalSettingsData>>('/settings/portal');
      return response.data.data;
    },
  });

  const [form, setForm] = useState<PortalSettingsData>({
    logoUrl: null,
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    fontColor: '#111827',
    firmName: '',
    welcomeMessage: '',
    termsOfService: '',
    portalLinkExpiryDays: 14,
  });

  const [initialized, setInitialized] = useState(false);
  if (settings && !initialized) {
    // Default fontColor if the tenant hasn't set one yet (older data may omit it).
    setForm({ ...settings, fontColor: settings.fontColor || '#111827' });
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: async (data: PortalSettingsData) => {
      const response = await apiClient.put<ApiResponse<PortalSettingsData>>(
        '/settings/portal',
        data,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal'] });
    },
  });

  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('logo', file);
      const response = await apiClient.post<ApiResponse<{ url: string }>>(
        '/settings/portal/logo',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setForm((prev) => ({ ...prev, logoUrl: data.url }));
      queryClient.invalidateQueries({ queryKey: ['settings', 'portal'] });
    },
  });

  const handleChange = (field: keyof PortalSettingsData, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoError('');

    if (!ACCEPTED_LOGO_FORMATS.includes(file.type)) {
      setLogoError('Logo must be PNG or SVG format.');
      return;
    }

    if (file.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      setLogoError(`Logo must be under ${MAX_LOGO_SIZE_MB} MB.`);
      return;
    }

    uploadLogoMutation.mutate(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(form);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Branding */}
      <Card>
        <CardHeader
          title="Portal Branding"
          description="Customize the client portal appearance with your firm's branding."
        />
        <CardContent>
          <div className="space-y-4">
            {/* Logo Upload */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Firm Logo
              </label>
              <div className="flex items-center gap-4">
                {form.logoUrl ? (
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-border bg-white p-2 dark:border-border-dark dark:bg-surface-dark-secondary">
                    <img
                      src={form.logoUrl}
                      alt="Firm logo"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex h-16 w-16 items-center justify-center rounded-lg border border-dashed border-border bg-surface-secondary text-gray-400 dark:border-border-dark dark:bg-surface-dark-tertiary">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V4.5a1.5 1.5 0 00-1.5-1.5H3.75a1.5 1.5 0 00-1.5 1.5v15a1.5 1.5 0 001.5 1.5z"
                      />
                    </svg>
                  </div>
                )}
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    loading={uploadLogoMutation.isPending}
                  >
                    Upload Logo
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.svg"
                    onChange={handleLogoUpload}
                    className="hidden"
                    aria-label="Upload firm logo"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    PNG or SVG, max {MAX_LOGO_SIZE_MB} MB
                  </p>
                  {logoError && (
                    <p className="mt-1 text-xs text-danger-500" role="alert">
                      {logoError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Primary Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.primaryColor}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-border dark:border-border-dark"
                    aria-label="Primary color picker"
                  />
                  <Input
                    value={form.primaryColor}
                    onChange={(e) => handleChange('primaryColor', e.target.value)}
                    placeholder="#2563eb"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Secondary Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.secondaryColor}
                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-border dark:border-border-dark"
                    aria-label="Secondary color picker"
                  />
                  <Input
                    value={form.secondaryColor}
                    onChange={(e) => handleChange('secondaryColor', e.target.value)}
                    placeholder="#1e40af"
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Font Color
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={form.fontColor}
                    onChange={(e) => handleChange('fontColor', e.target.value)}
                    className="h-10 w-10 cursor-pointer rounded border border-border dark:border-border-dark"
                    aria-label="Font color picker"
                  />
                  <Input
                    value={form.fontColor}
                    onChange={(e) => handleChange('fontColor', e.target.value)}
                    placeholder="#111827"
                    className="flex-1"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Default text color used across the client portal.
                </p>
              </div>
            </div>

            <Input
              label="Firm Name (displayed on portal)"
              value={form.firmName}
              onChange={(e) => handleChange('firmName', e.target.value)}
              placeholder="Your Firm Name"
            />
          </div>
        </CardContent>
      </Card>

      {/* Portal Content */}
      <Card>
        <CardHeader
          title="Portal Content"
          description="Welcome message, terms, and link settings displayed to clients."
        />
        <CardContent>
          <div className="space-y-4">
            <Input
              label="Portal Link Expiry (days)"
              type="number"
              value={String(form.portalLinkExpiryDays)}
              onChange={(e) => handleChange('portalLinkExpiryDays', Number(e.target.value))}
              min={1}
              max={90}
              hint="Default expiry for invitation links (1–90 days)"
            />
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Welcome Message
              </label>
              <textarea
                value={form.welcomeMessage}
                onChange={(e) => handleChange('welcomeMessage', e.target.value)}
                rows={3}
                className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                placeholder="Welcome to our client portal. Here you can track your jobs, upload documents, and view invoices."
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Terms of Service
              </label>
              <textarea
                value={form.termsOfService}
                onChange={(e) => handleChange('termsOfService', e.target.value)}
                rows={4}
                className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                placeholder="By using this portal, you agree to..."
              />
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button type="button" variant="outline" onClick={() => settings && setForm(settings)}>
            Reset
          </Button>
          <Button type="submit" loading={updateMutation.isPending}>
            Save Portal Settings
          </Button>
        </CardFooter>
      </Card>
    </form>
  );
}
